import type { TemplateResult } from 'lit';

/** Direction used when sorting table columns. */
export type DataTableSortDirection = 'asc' | 'desc';

/** Describes the sort order for a single column. */
export interface DataTableSortDescriptor {
  field: string;
  direction: DataTableSortDirection;
}

/**
 * Operators supported by structured filters. Implementations can ignore
 * operators they do not understand and respond with a validation error.
 */
export type DataTableFilterOperator =
  | 'eq'
  | 'neq'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte';

/** Represents a structured filter applied to a column or field. */
export interface DataTableFilterDescriptor<TValue = unknown> {
  field: string;
  operator?: DataTableFilterOperator;
  value: TValue;
}

/**
 * Request payload all table data providers should consume. Consumers should
 * treat filters/sort descriptors as immutable per request.
 */
export interface DataTableDataRequest {
  sort: DataTableSortDescriptor[];
  filters: DataTableFilterDescriptor[];
  freeText?: string;
  requiresCount?: boolean;
  sliceStart: number;
  sliceLength: number;
}

/**
 * Response payload emitted by data providers feeding the table.
 * `hasMore` should be computed using the "fetch one extra row" heuristic.
 */
export interface DataTableDataResponse<TItem = unknown> {
  items: TItem[];
  totalCount?: number;
  hasMore: boolean;
}

/** Function signature every table data provider should implement. */
export type DataTableDataProvider<TItem = unknown> = (
  request: DataTableDataRequest
) => Promise<DataTableDataResponse<TItem>>;

/**
 * Hook used by future adapters that inject free-form row templates or
 * additional metadata without leaking provider internals into the table.
 */
export interface DataTableDataHook<TItem = unknown> {
  template?: TemplateResult | (() => TemplateResult);
  item: TItem;
}

/**
 * Options for configuring the array data provider.
 */
export interface ArrayDataProviderOptions<TItem> {
  /**
   * Optional function to filter items based on free-text search.
   * If not provided, no free-text filtering is applied.
   */
  filterFn?: (item: TItem, searchText: string) => boolean;

  /**
   * Optional function to get the value of a field for sorting.
   * If not provided, uses direct property access.
   */
  getFieldValue?: (item: TItem, field: string) => unknown;
}

/**
 * Creates a reusable DataTableDataProvider from a static array of items.
 * Supports pagination, free-text filtering, and sorting on client-side data.
 *
 * @param getItems - Function that returns the current array of items (called on each request)
 * @param options - Optional configuration for filtering and sorting
 * @returns A DataTableDataProvider that can be used with app-data-table
 *
 * @example
 * ```ts
 * // Basic usage with a reactive array
 * this.provider = createArrayDataProvider(
 *   () => this.assignments,
 *   {
 *     filterFn: (item, text) =>
 *       item.name.toLowerCase().includes(text.toLowerCase())
 *   }
 * );
 * ```
 */
export function createArrayDataProvider<TItem>(
  getItems: () => TItem[],
  options: ArrayDataProviderOptions<TItem> = {}
): DataTableDataProvider<TItem> {
  const { filterFn, getFieldValue } = options;

  return async (
    request: DataTableDataRequest
  ): Promise<DataTableDataResponse<TItem>> => {
    let items = [...getItems()];

    // Apply free-text filter if provided
    if (request.freeText && filterFn) {
      const searchText = request.freeText.toLowerCase();
      items = items.filter((item) => filterFn(item, searchText));
    }

    // Apply structured filters
    for (const filter of request.filters) {
      const getValue = getFieldValue
        ? (item: TItem) => getFieldValue(item, filter.field)
        : (item: TItem) => (item as Record<string, unknown>)[filter.field];

      items = items.filter((item) => {
        const value = getValue(item);
        const filterValue = filter.value;

        switch (filter.operator) {
          case 'eq':
            return value === filterValue;
          case 'neq':
            return value !== filterValue;
          case 'contains':
            return String(value ?? '')
              .toLowerCase()
              .includes(String(filterValue ?? '').toLowerCase());
          case 'startsWith':
            return String(value ?? '')
              .toLowerCase()
              .startsWith(String(filterValue ?? '').toLowerCase());
          case 'endsWith':
            return String(value ?? '')
              .toLowerCase()
              .endsWith(String(filterValue ?? '').toLowerCase());
          case 'gt':
            return (value as number) > (filterValue as number);
          case 'gte':
            return (value as number) >= (filterValue as number);
          case 'lt':
            return (value as number) < (filterValue as number);
          case 'lte':
            return (value as number) <= (filterValue as number);
          default:
            // Default to equality if operator unknown
            return value === filterValue;
        }
      });
    }

    // Apply sorting
    if (request.sort.length > 0) {
      items.sort((a, b) => {
        for (const sortDesc of request.sort) {
          const getValue = getFieldValue
            ? (item: TItem) => getFieldValue(item, sortDesc.field)
            : (item: TItem) =>
                (item as Record<string, unknown>)[sortDesc.field];

          const aVal = getValue(a);
          const bVal = getValue(b);

          let comparison = 0;
          if (aVal == null && bVal == null) comparison = 0;
          else if (aVal == null) comparison = -1;
          else if (bVal == null) comparison = 1;
          else if (typeof aVal === 'string' && typeof bVal === 'string') {
            comparison = aVal.localeCompare(bVal);
          } else if (typeof aVal === 'number' && typeof bVal === 'number') {
            comparison = aVal - bVal;
          } else {
            comparison = String(aVal).localeCompare(String(bVal));
          }

          if (comparison !== 0) {
            return sortDesc.direction === 'desc' ? -comparison : comparison;
          }
        }
        return 0;
      });
    }

    const totalCount = items.length;
    const start = request.sliceStart ?? 0;
    const length = request.sliceLength ?? totalCount;
    const page = items.slice(start, start + length);
    const hasMore = start + page.length < totalCount;

    return {
      items: page,
      totalCount,
      hasMore,
    };
  };
}
